/* eslint-disable react/prop-types, prettier/prettier */
import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import SearchIcon from '@mui/icons-material/Search'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import SendIcon from '@mui/icons-material/Send'
import { keyframes } from '@emotion/react'
import AIService from '../../services/aiService'
import ErrorBanner from '../../components/shared/ErrorBanner'
import {
    ACCENT,
    CATEGORY,
    FONT,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    menuProps,
    pageSx,
    cardSx,
    tint,
} from '../../theme/tokens'

const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`

const LOOKBACK_OPTIONS = [
    { label: '7 days', value: 7 },
    { label: '14 days', value: 14 },
    { label: '30 days', value: 30 },
    { label: '60 days', value: 60 },
]

const IDENTITY_TYPES = ['email', 'mobile_no']

function StatCard({ label, value, color, sublabel }) {
    return (
        <Paper
            elevation={0}
            sx={{
                ...cardSx,
                border: `1px solid ${tint(color, 0.3)}`,
                p: 2.5,
                textAlign: 'center',
            }}
        >
            <Typography variant="h1" sx={{ color, lineHeight: 1 }}>
                {value}
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: TEXT.primary, fontWeight: 600, mt: 0.5 }}
            >
                {label}
            </Typography>
            {sublabel && (
                <Typography
                    variant="caption"
                    component="p"
                    sx={{ color: TEXT.muted, mt: 0.25 }}
                >
                    {sublabel}
                </Typography>
            )}
        </Paper>
    )
}

// Each source's label colour: text-safe on its tint in both themes.
// Salesforce's own blue is 2.9:1 on white, so its label takes the
// categorical blue.
export const SOURCE_META = {
    commlogs: { label: 'CommLogs', color: CATEGORY.cyan },
    salesforce: { label: 'SFDC', color: CATEGORY.blue },
}
const UNKNOWN_SOURCE_COLOR = TEXT.muted

function SourceBadge({ source }) {
    const meta = SOURCE_META[source] ?? {
        label: source,
        color: UNKNOWN_SOURCE_COLOR,
    }
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                typography: 'caption',
                fontWeight: 700,
                lineHeight: 1.4,
                px: 0.6,
                py: 0.1,
                mr: 0.75,
                borderRadius: `${RADIUS.xs}px`,
                background: tint(meta.color, 0.12),
                color: meta.color,
                border: `1px solid ${tint(meta.color, 0.3)}`,
                verticalAlign: 'middle',
            }}
        >
            {meta.label}
        </Box>
    )
}

/** Per-source check status strip shown above the results. */
function SourcesStrip({ sources, navigate }) {
    if (!sources?.length) return null
    const failed = sources.filter((s) => !s.ok)
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
            }}
        >
            <Typography variant="overline" sx={{ color: TEXT.secondary }}>
                Sources
            </Typography>
            {sources.map((s) => {
                const meta = SOURCE_META[s.source] ?? {
                    label: s.source,
                    color: UNKNOWN_SOURCE_COLOR,
                }
                return (
                    <Chip
                        key={s.source}
                        icon={
                            s.ok ? (
                                <CheckCircleIcon
                                    sx={{
                                        fontSize: `${ICON.xs}px !important`,
                                        color: `${meta.color} !important`,
                                    }}
                                />
                            ) : (
                                <WarningAmberIcon
                                    sx={{
                                        fontSize: `${ICON.xs}px !important`,
                                        color: `${STATUS.warning.fg} !important`,
                                    }}
                                />
                            )
                        }
                        label={
                            s.ok
                                ? `${meta.label} · checked`
                                : `${meta.label} · ${
                                      s.reason === 'not_connected'
                                          ? 'not connected'
                                          : 'unavailable'
                                  }`
                        }
                        size="small"
                        sx={{
                            background: s.ok
                                ? tint(meta.color, 0.08)
                                : STATUS.warning.bg,
                            color: s.ok ? meta.color : STATUS.warning.fg,
                            border: `1px solid ${
                                s.ok
                                    ? tint(meta.color, 0.3)
                                    : STATUS.warning.border
                            }`,
                            typography: 'caption',
                            fontWeight: 600,
                        }}
                    />
                )
            })}
            {failed.some((s) => s.reason === 'not_connected') && (
                <Button
                    size="small"
                    onClick={() => navigate('/settings')}
                    sx={{
                        typography: 'caption',
                        color: ACCENT.text,
                        p: 0,
                        minWidth: 0,
                        '&:hover': { background: 'transparent', opacity: 0.8 },
                    }}
                >
                    Connect in Settings →
                </Button>
            )}
        </Box>
    )
}

function OverlapRow({ result, index }) {
    const isOverlap = result.status === 'overlap'
    const tone = isOverlap ? STATUS.warning : STATUS.success

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 1.25,
                borderBottom: `1px solid ${SURFACE.border}`,
                animation: `${fadeIn} ${0.1 + index * 0.03}s ease`,
                '&:last-child': { borderBottom: 'none' },
            }}
        >
            <Box>
                <Typography
                    variant="body2"
                    sx={{ color: TEXT.primary, fontFamily: FONT.mono }}
                >
                    {result.identity}
                </Typography>
                {isOverlap &&
                    (result.campaigns ?? []).map((c, i) => (
                        <Typography
                            key={i}
                            variant="caption"
                            component="p"
                            sx={{ color: TEXT.muted, mt: 0.25 }}
                        >
                            {c.source && <SourceBadge source={c.source} />}
                            {c.event_type} ({c.count} send
                            {c.count !== 1 ? 's' : ''})
                        </Typography>
                    ))}
            </Box>
            <Typography
                variant="caption"
                sx={{ color: TEXT.muted, textAlign: 'right' }}
            >
                {result.campaign_count} campaign
                {result.campaign_count !== 1 ? 's' : ''}
            </Typography>
            <Chip
                icon={
                    isOverlap ? (
                        <WarningAmberIcon
                            sx={{
                                fontSize: `${ICON.xs}px !important`,
                                color: `${STATUS.warning.fg} !important`,
                            }}
                        />
                    ) : (
                        <CheckCircleIcon
                            sx={{
                                fontSize: `${ICON.xs}px !important`,
                                color: `${STATUS.success.fg} !important`,
                            }}
                        />
                    )
                }
                label={isOverlap ? 'Overlap' : 'Clean'}
                size="small"
                sx={{
                    background: tone.bg,
                    color: tone.fg,
                    border: `1px solid ${tone.border}`,
                    typography: 'caption',
                    fontWeight: 600,
                }}
            />
        </Box>
    )
}

export default function AudienceOverlap() {
    const navigate = useNavigate()
    const location = useLocation()
    const [identityText, setIdentityText] = useState('')
    const [identityType, setIdentityType] = useState('email')
    const [lookbackDays, setLookbackDays] = useState(30)
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState('')
    const [filter, setFilter] = useState('all')

    useEffect(() => {
        // Reset outputs so re-runs (navigation with new state) behave predictably
        setResult(null)
        setError('')
        const state = location.state
        // Only a hand-off fills the box. Sample addresses typed into the real
        // input were checked as if they were the audience; the placeholder
        // shows the format instead.
        setIdentityText(
            Array.isArray(state?.identities) ? state.identities.join('\n') : ''
        )
        // 'mobile' is what CRM Enrich used to send for mobile numbers.
        const type =
            state?.identity_type === 'mobile'
                ? 'mobile_no'
                : state?.identity_type
        setIdentityType(IDENTITY_TYPES.includes(type) ? type : 'email')
    }, [location.state])

    const handleCheck = async () => {
        const lines = identityText
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean)
        if (!lines.length) return
        setLoading(true)
        setError('')
        setResult(null)
        try {
            const res = await AIService.audienceOverlap({
                identities: lines,
                identity_type: identityType,
                lookback_days: lookbackDays,
            })
            setResult(res.data?.result)
        } catch {
            setError('Failed to check overlap. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const identityCount = identityText
        .split('\n')
        .filter((l) => l.trim()).length

    const filteredResults = (result?.results ?? []).filter((r) =>
        filter === 'all' ? true : r.status === filter
    )

    const getCleanContactsCsv = () => {
        if (!result?.results) return ''
        const cleanResults = (result.results ?? []).filter(
            (r) => r.status === 'clean'
        )
        const header = identityType === 'email' ? 'email' : 'mobile'
        return [header, ...cleanResults.map((r) => r.identity)].join('\n')
    }

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 3, md: 5 } }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 3,
                    }}
                >
                    <Box
                        sx={{
                            background: GRADIENT.brand,
                            borderRadius: `${RADIUS.md}px`,
                            p: 1,
                            display: 'flex',
                        }}
                    >
                        {/* Deep Blue on the brand gradient (white was 1.8:1 on its yellow). */}
                        <PeopleAltIcon
                            sx={{ color: ON_ORANGE, fontSize: ICON.lg }}
                        />
                    </Box>
                    <Box>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{ color: TEXT.primary }}
                        >
                            Audience Overlap
                        </Typography>
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{ color: TEXT.secondary }}
                        >
                            Check if recipients were targeted in other campaigns
                            within a lookback window — across CommLogs and
                            Salesforce
                        </Typography>
                    </Box>
                </Box>

                {/* Form */}
                <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mb: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Box>
                            <Typography
                                variant="caption"
                                component="p"
                                sx={{ color: TEXT.secondary, mb: 0.5 }}
                            >
                                Identity Type
                            </Typography>
                            <ToggleButtonGroup
                                value={identityType}
                                exclusive
                                onChange={(_, v) => v && setIdentityType(v)}
                                size="small"
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        color: TEXT.secondary,
                                        border: `1px solid ${SURFACE.borderHover}`,
                                        px: 2,
                                        py: 0.5,
                                        typography: 'caption',
                                        textTransform: 'none',
                                        '&.Mui-selected, &.Mui-selected:hover':
                                            {
                                                background: tint(
                                                    CATEGORY.cyan,
                                                    0.15
                                                ),
                                                color: CATEGORY.cyan,
                                                borderColor: tint(
                                                    CATEGORY.cyan,
                                                    0.35
                                                ),
                                            },
                                    },
                                }}
                            >
                                <ToggleButton value="email">Email</ToggleButton>
                                <ToggleButton value="mobile_no">
                                    Mobile
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <Box>
                            <Typography
                                variant="caption"
                                component="p"
                                sx={{ color: TEXT.secondary, mb: 0.5 }}
                            >
                                Lookback Window
                            </Typography>
                            <TextField
                                select
                                value={lookbackDays}
                                onChange={(e) =>
                                    setLookbackDays(Number(e.target.value))
                                }
                                size="small"
                                SelectProps={{ MenuProps: menuProps }}
                                sx={{ width: 130 }}
                            >
                                {LOOKBACK_OPTIONS.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </Box>

                    <TextField
                        label={`Paste ${
                            identityType === 'email'
                                ? 'email addresses'
                                : 'mobile numbers'
                        } (one per line)`}
                        value={identityText}
                        onChange={(e) => setIdentityText(e.target.value)}
                        multiline
                        rows={7}
                        fullWidth
                        placeholder={
                            identityType === 'email'
                                ? 'parent1@example.com\nparent2@example.com\n...'
                                : '+919876543210\n+919876543211\n...'
                        }
                        sx={{ '& .MuiFormHelperText-root': { ml: 0 } }}
                        helperText={
                            identityCount > 0
                                ? `${identityCount} identit${
                                      identityCount !== 1 ? 'ies' : 'y'
                                  } detected`
                                : ''
                        }
                        FormHelperTextProps={{
                            sx: { color: CATEGORY.cyan, typography: 'caption' },
                        }}
                    />

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleCheck}
                        disabled={loading || !identityCount}
                        startIcon={
                            loading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <SearchIcon />
                            )
                        }
                        sx={{ mt: 2, py: 1.25 }}
                    >
                        {loading
                            ? 'Checking overlap…'
                            : `Check ${identityCount || ''} Identit${
                                  identityCount !== 1 ? 'ies' : 'y'
                              }`}
                    </Button>
                </Paper>

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}

                {/* Results */}
                {result && (
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            animation: `${fadeIn} 0.3s ease`,
                        }}
                    >
                        <SourcesStrip
                            sources={result.sources}
                            navigate={navigate}
                        />

                        {/* Stats */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                                gap: 2,
                            }}
                        >
                            <StatCard
                                label="Checked"
                                value={result.total}
                                color={CATEGORY.cyan}
                            />
                            <StatCard
                                label="Overlap"
                                value={result.overlapping}
                                color={STATUS.warning.fg}
                                sublabel="in multiple campaigns"
                            />
                            <StatCard
                                label="Clean"
                                value={result.clean}
                                color={STATUS.success.fg}
                                sublabel="no overlap found"
                            />
                            <StatCard
                                label="Overlap Rate"
                                value={`${result.overlap_pct}%`}
                                color={
                                    result.overlap_pct > 30
                                        ? STATUS.error.fg
                                        : result.overlap_pct > 10
                                        ? STATUS.warning.fg
                                        : STATUS.success.fg
                                }
                            />
                        </Box>

                        {/* Results table */}
                        <Paper
                            elevation={0}
                            sx={{ ...cardSx, overflow: 'hidden' }}
                        >
                            {/* Filter bar */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 2,
                                    py: 1.5,
                                    borderBottom: `1px solid ${SURFACE.border}`,
                                }}
                            >
                                <Typography
                                    variant="overline"
                                    sx={{ color: TEXT.secondary, mr: 1 }}
                                >
                                    Filter
                                </Typography>
                                {['all', 'overlap', 'clean'].map((f) => (
                                    <Chip
                                        key={f}
                                        label={
                                            f === 'all'
                                                ? `All (${result.total})`
                                                : f === 'overlap'
                                                ? `Overlap (${result.overlapping})`
                                                : `Clean (${result.clean})`
                                        }
                                        size="small"
                                        onClick={() => setFilter(f)}
                                        // The chosen filter in the pagination's selected style:
                                        // primary text on the orange tint, an orange outline.
                                        sx={{
                                            cursor: 'pointer',
                                            background:
                                                filter === f
                                                    ? ACCENT.bg15
                                                    : SURFACE.hover,
                                            color:
                                                filter === f
                                                    ? TEXT.primary
                                                    : TEXT.secondary,
                                            border: `1px solid ${
                                                filter === f
                                                    ? ACCENT.strong
                                                    : SURFACE.border
                                            }`,
                                            fontWeight:
                                                filter === f ? 700 : 400,
                                            typography: 'caption',
                                        }}
                                    />
                                ))}
                            </Box>

                            {filteredResults.length === 0 ? (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: TEXT.faint,
                                        p: 3,
                                        textAlign: 'center',
                                    }}
                                >
                                    No results match this filter.
                                </Typography>
                            ) : (
                                filteredResults.map((r, i) => (
                                    <OverlapRow
                                        key={r.identity}
                                        result={r}
                                        index={i}
                                    />
                                ))
                            )}
                        </Paper>

                        {/* CTAs */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                mt: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<SendIcon />}
                                onClick={() =>
                                    navigate('/bulk-send', {
                                        state: {
                                            csvText: getCleanContactsCsv(),
                                        },
                                    })
                                }
                            >
                                Proceed to Bulk Send (Clean only)
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
